import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, CheckCircle, Vote, Lock, Play, X, ExternalLink,
  Users, Mic, Sparkles, Disc3, PenTool, Music, AlertCircle, ThumbsUp
} from 'lucide-react';
import { usePageVisibility } from '../hooks/usePageVisibility';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  groupe: Users,
  soliste: Mic,
  nouveau: Sparkles,
  album: Disc3,
  compositeur: PenTool,
  titre: Music,
};

const Voter = () => {
  const { user, token } = useAuth();
  usePageVisibility('page_voter_enabled');
  const [categories, setCategories] = useState([]);
  const [finalists, setFinalists] = useState({});
  const [myVotes, setMyVotes] = useState([]);
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [votingFor, setVotingFor] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [catRes, finalistsRes, phaseRes, votesRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/proposals?finalists_only=true`),
        axios.get(`${API}/phase`),
        axios.get(`${API}/votes/my`)
      ]);
      
      setCategories(catRes.data);
      setPhase(phaseRes.data);
      setMyVotes(votesRes.data);
      
      // Group finalists by category
      const grouped = {};
      finalistsRes.data.forEach(nom => {
        if (!grouped[nom.category_id]) {
          grouped[nom.category_id] = [];
        }
        grouped[nom.category_id].push(nom);
      });
      // Sort by vote_count
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => b.vote_count - a.vote_count);
      });
      setFinalists(grouped);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (proposalId, categoryId) => {
    if (!phase?.phase2_open) {
      toast.error('Le vote n\'est pas encore ouvert');
      return;
    }
    
    setVotingFor(proposalId);
    try {
      await axios.post(`${API}/votes`, {
        proposal_id: proposalId,
        category_id: categoryId
      });
      toast.success('Vote enregistré avec succès !', {
        description: 'Merci pour votre participation.'
      });
      setMyVotes(prev => [...prev, { proposal_id: proposalId, category_id: categoryId }]);
      // Refresh finalists to update counts
      const finalistsRes = await axios.get(`${API}/proposals?finalists_only=true`);
      const grouped = {};
      finalistsRes.data.forEach(nom => {
        if (!grouped[nom.category_id]) grouped[nom.category_id] = [];
        grouped[nom.category_id].push(nom);
      });
      Object.keys(grouped).forEach(key => {
        grouped[key].sort((a, b) => b.vote_count - a.vote_count);
      });
      setFinalists(grouped);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors du vote';
      toast.error(msg);
    } finally {
      setVotingFor(null);
    }
  };

  const hasVotedInCategory = (categoryId) => {
    return myVotes.some(v => v.category_id === categoryId);
  };

  const hasVotedFor = (proposalId) => {
    return myVotes.some(v => v.proposal_id === proposalId);
  };

  const getCategoryData = (catId) => categories.find(c => c.id === catId);
  
  const isPhase2Open = phase?.phase2_open;
  const totalFinalists = Object.values(finalists).flat().length;

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian pt-32 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-obsidian pt-32 pb-20" data-testid="voter-page">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mb-12">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 border border-electric-blue/30 bg-electric-blue/5">
            <span className="text-xs uppercase tracking-widest text-electric-cyan">Phase 2 — Vote Final</span>
          </div>
          <h1 className="font-syne text-4xl md:text-5xl font-extrabold tracking-tighter uppercase text-white mb-4">
            Votez pour<br />
            <span className="text-gold-gradient">Vos Favoris</span>
          </h1>
          <p className="text-lg text-white/60">
            Les 4 finalistes de chaque catégorie ont été sélectionnés. 
            Découvrez leur univers musical et votez pour désigner les lauréats 2026.
          </p>
        </div>

        {/* Phase Status */}
        {!isPhase2Open && (
          <div className="mt-6 p-4 border border-orange-500/30 bg-orange-500/10 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <div>
              <p className="text-orange-300 font-medium">Le vote n'est pas encore ouvert</p>
              <p className="text-sm text-orange-300/70">{phase?.phase1_message || "La sélection des finalistes est en cours."}</p>
            </div>
          </div>
        )}

        {isPhase2Open && (
          <div className="mt-6 p-4 border border-green-500/30 bg-green-500/10 flex items-center gap-3">
            <Vote className="w-5 h-5 text-green-400 flex-shrink-0" />
            <div>
              <p className="text-green-300 font-medium">Le vote est ouvert !</p>
              <p className="text-sm text-green-300/70">Vous pouvez voter une fois par catégorie.</p>
            </div>
          </div>
        )}
      </section>

      {/* No Finalists */}
      {totalFinalists === 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center py-20 border border-white/10 bg-obsidian-light">
            <Vote className="w-16 h-16 text-white/20 mx-auto mb-6" />
            <h2 className="font-syne text-2xl font-bold text-white mb-4">Finalistes à venir</h2>
            <p className="text-white/50 max-w-md mx-auto mb-8">
              Les finalistes seront annoncés à la fin de la phase de propositions. 
              En attendant, proposez vos artistes favoris !
            </p>
            <Link
              to="/proposer"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-6 py-3 text-sm transition-all"
            >
              Proposer un candidat
            </Link>
          </div>
        </section>
      )}

      {/* Categories & Voting */}
      {totalFinalists > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="space-y-20">
            {categories.map(category => {
              const Icon = categoryIcons[category.id] || Music;
              const categoryFinalists = finalists[category.id] || [];
              const votedInCategory = hasVotedInCategory(category.id);

              if (categoryFinalists.length === 0) return null;

              return (
                <div key={category.id} data-testid={`voting-category-${category.id}`}>
                  {/* Category Header */}
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8 pb-4 border-b border-white/10">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 flex items-center justify-center border border-gold/30 bg-gold/5">
                        <Icon className="w-7 h-7 text-gold" />
                      </div>
                      <div>
                        <h2 className="font-syne text-2xl font-bold text-white">{category.name}</h2>
                        <p className="text-sm text-white/40 italic">{category.name_breton}</p>
                      </div>
                    </div>
                    {votedInCategory && (
                      <div className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/30">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        <span className="text-sm text-green-400">Vous avez voté</span>
                      </div>
                    )}
                  </div>

                  {/* Finalists Grid */}
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {categoryFinalists.map((finalist, idx) => {
                      const isVoted = hasVotedFor(finalist.id);
                      const isVoting = votingFor === finalist.id;
                      const canVote = isPhase2Open && !votedInCategory;
                      
                      return (
                        <div
                          key={finalist.id}
                          className={`border transition-all relative overflow-hidden group ${
                            isVoted
                              ? 'border-gold bg-gold/5'
                              : 'border-white/10 bg-obsidian-light hover:border-gold/30'
                          }`}
                          data-testid={`finalist-${finalist.id}`}
                        >
                          {/* Rank Badge */}
                          {idx < 3 && (
                            <div className={`absolute top-3 left-3 z-10 w-8 h-8 flex items-center justify-center font-syne font-bold ${
                              idx === 0 ? 'bg-gold text-obsidian' : 'bg-white/10 text-white'
                            }`}>
                              {idx + 1}
                            </div>
                          )}
                          
                          {/* Video Thumbnail */}
                          <div 
                            className="aspect-video bg-obsidian relative overflow-hidden cursor-pointer"
                            onClick={() => finalist.video_embed_url && setSelectedVideo(finalist)}
                          >
                            {finalist.video_thumbnail ? (
                              <img 
                                src={finalist.video_thumbnail} 
                                alt={finalist.nominee_name}
                                className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gold/10 to-electric-blue/10">
                                <Icon className="w-16 h-16 text-white/10" />
                              </div>
                            )}
                            
                            {/* Play Button Overlay */}
                            {finalist.video_embed_url && (
                              <div className="absolute inset-0 flex items-center justify-center bg-obsidian/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-14 h-14 rounded-full bg-gold flex items-center justify-center transform group-hover:scale-110 transition-transform">
                                  <Play className="w-6 h-6 text-obsidian ml-1" />
                                </div>
                              </div>
                            )}
                            
                            {/* Vote Count */}
                            <div className="absolute bottom-2 right-2 px-2 py-1 bg-obsidian/80 backdrop-blur-sm text-xs text-white/70 flex items-center gap-1">
                              <ThumbsUp className="w-3 h-3" />
                              {finalist.vote_count || 0}
                            </div>
                            
                            {isVoted && (
                              <div className="absolute top-3 right-3 w-8 h-8 bg-gold rounded-full flex items-center justify-center">
                                <CheckCircle className="w-5 h-5 text-obsidian" />
                              </div>
                            )}
                          </div>

                          {/* Content */}
                          <div className="p-5">
                            <h3 className="font-syne font-bold text-white text-lg mb-2 line-clamp-1">
                              {finalist.nominee_name}
                            </h3>
                            <p className="text-sm text-white/50 line-clamp-2 mb-4 min-h-[40px]">
                              {finalist.description}
                            </p>

                            {/* Vote Button */}
                            <button
                              onClick={() => handleVote(finalist.id, category.id)}
                              disabled={!canVote || isVoting}
                              className={`w-full py-3 text-sm uppercase tracking-wider font-bold transition-all flex items-center justify-center gap-2 ${
                                isVoted
                                  ? 'bg-gold text-obsidian cursor-default'
                                  : votedInCategory
                                  ? 'bg-white/5 text-white/30 cursor-not-allowed'
                                  : canVote
                                  ? 'bg-white/5 hover:bg-gold hover:text-obsidian text-white border border-white/10 hover:border-gold'
                                  : 'bg-white/5 text-white/30 cursor-not-allowed'
                              }`}
                              data-testid={`vote-btn-${finalist.id}`}
                            >
                              {isVoting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : isVoted ? (
                                <>
                                  <CheckCircle className="w-4 h-4" />
                                  Votre choix
                                </>
                              ) : !isPhase2Open ? (
                                <>
                                  <Lock className="w-4 h-4" />
                                  Vote fermé
                                </>
                              ) : votedInCategory ? (
                                'Déjà voté'
                              ) : (
                                <>
                                  <Vote className="w-4 h-4" />
                                  Voter
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-obsidian/95 backdrop-blur-sm"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="w-full max-w-4xl"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-gold">{getCategoryData(selectedVideo.category_id)?.name}</p>
                <h3 className="font-syne text-xl font-bold text-white">{selectedVideo.nominee_name}</h3>
              </div>
              <button 
                onClick={() => setSelectedVideo(null)}
                className="p-2 text-white/50 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="aspect-video bg-obsidian border border-white/10">
              <iframe
                src={selectedVideo.video_embed_url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={selectedVideo.nominee_name}
              />
            </div>
            
            <div className="mt-4 flex items-center justify-between">
              <p className="text-white/50 text-sm max-w-2xl">{selectedVideo.description}</p>
              
              {isPhase2Open && !hasVotedInCategory(selectedVideo.category_id) && (
                <button
                  onClick={() => {
                    handleVote(selectedVideo.id, selectedVideo.category_id);
                    setSelectedVideo(null);
                  }}
                  className="bg-gold hover:bg-gold-glow text-obsidian font-bold uppercase tracking-widest px-6 py-3 text-sm transition-all flex items-center gap-2 flex-shrink-0"
                >
                  <Vote className="w-4 h-4" />
                  Voter
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bottom CTA */}
      {totalFinalists > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-20">
          <div className="p-8 border border-white/10 bg-obsidian-light text-center">
            <h3 className="font-syne text-xl font-bold text-white mb-4">
              {myVotes.length > 0 
                ? `Vous avez voté dans ${myVotes.length} catégorie${myVotes.length > 1 ? 's' : ''}`
                : 'Découvrez tous les finalistes'
              }
            </h3>
            <p className="text-white/50 mb-6">
              {myVotes.length < categories.length && isPhase2Open
                ? `Il vous reste ${categories.length - myVotes.length} catégorie${categories.length - myVotes.length > 1 ? 's' : ''} où voter.`
                : 'Les résultats seront annoncés lors du gala.'
              }
            </p>
            <Link
              to="/gala"
              className="inline-flex items-center gap-2 border border-gold/30 hover:border-gold text-gold px-6 py-3 text-sm uppercase tracking-wider hover:bg-gold/5 transition-all"
            >
              Découvrir le Gala
            </Link>
          </div>
        </section>
      )}
    </main>
  );
};

export default Voter;
